import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "common/redux/core/root.reducer";
import {
  setSelectedDocumentType,
  fetchDocumentTypes
} from "../redux/document-type/document-type.slice";
import { DropdownOption } from "../constants/dropdown-options";
import "./UnifiedDropdown.scss";
import { Dialog } from "@mui/material";
import ManageDocumentTypesDialog from "./ManageDocumentTypesDialog";

export interface UnifiedDropdownProps {
  // Option 1: Use predefined document types from Redux
  type?: "document-type";
  useRedux?: boolean;

  // Option 2: Use custom options
  options?: DropdownOption[];

  // Common props
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  required?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const UnifiedDropdown: React.FC<UnifiedDropdownProps> = ({
  type,
  useRedux = false,
  options,
  value,
  onChange,
  label = "Select option",
  required = false,
  className = "",
  placeholder,
  disabled = false
}) => {
  const dispatch = useDispatch();
  const { documentTypeOptions, selectedDocumentType, loading, error } =
    useSelector((state: RootState) => state.documentType);
  const [openManageModal, setOpenManageModal] = React.useState(false);
  // Fetch document types when component mounts and using document-type with Redux
  useEffect(() => {
    if (type === "document-type" && useRedux) {
      dispatch(fetchDocumentTypes() as any);
    }
  }, [type, useRedux, dispatch]);

  const MANAGE_VALUE = "__MANAGE__";

  const dropdownOptionsWithManage =
    type === "document-type"
      ? [
          ...documentTypeOptions
          // { label: "➕ Manage Documents Type", value: MANAGE_VALUE }
        ]
      : options || [];

  // Determine which options to use
  const dropdownOptions =
    type === "document-type" ? documentTypeOptions : options || [];

  // Determine current value
  const currentValue =
    useRedux && type === "document-type" ? selectedDocumentType : value;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;

    // 🔹 Open manage modal instead of selecting
    if (newValue === MANAGE_VALUE) {
      setOpenManageModal(true);
      return;
    }

    // Handle Redux updates for document types
    if (useRedux && type === "document-type") {
      dispatch(setSelectedDocumentType(newValue));
    }

    // Always call onChange if provided
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <>
      <div className={`form-field unified-dropdown ${className}`}>
        <label>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </label>
        <select
          value={currentValue || ""}
          onChange={handleChange}
          disabled={disabled || loading}
          className={disabled || loading ? "disabled" : ""}
        >
          {placeholder && (
            <option value="" disabled>
              {loading ? "Loading..." : placeholder}
            </option>
          )}
          {/* dropdownOptions */}

          {dropdownOptionsWithManage?.map((option: DropdownOption) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && type === "document-type" && (
          <div
            className="error-message"
            style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
          >
            {error.join(", ")}
          </div>
        )}
      </div>

      <Dialog
        open={openManageModal}
        onClose={() => setOpenManageModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <ManageDocumentTypesDialog onClose={() => setOpenManageModal(false)} />
      </Dialog>
    </>
  );
};

export default UnifiedDropdown;
